import { computed, onBeforeUnmount, onMounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import {
  getCoursewareAudioUrl,
  requestCoursewareAudio,
  CoursewareAudioItem,
} from './coursewareAudioApi';

function formatAudioTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) {
    return '0:00';
  }
  const totalSeconds = Math.floor(value);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function useCoursewareAudio() {
  const route = useRoute();
  const store = useStore();

  const state = reactive({
    audioEl: null as HTMLAudioElement | null,
    audioList: [] as CoursewareAudioItem[],
    audioUrl: '',
    loading: false,
    playing: false,
    muted: false,
    seekValue: 0,
    isSeeking: false,
    currentTime: 0,
    duration: 0,
  });

  const coursewareIndex = computed(() => Number(store.getters.getCoursewareIndex ?? 0));
  const topicId = computed(() => String(route.query.topicId ?? ''));
  const visible = computed(() => Boolean(state.audioUrl) || state.loading);
  const currentTimeLabel = computed(() => formatAudioTime(state.currentTime));
  const durationLabel = computed(() => formatAudioTime(state.duration));
  const progressPercent = computed(() => {
    if (!state.duration) {
      return 0;
    }
    return Math.min(100, (state.currentTime / state.duration) * 100);
  });

  function bindAudioRef(el: HTMLAudioElement | null): void {
    state.audioEl = el;
  }

  function resetPlayback(): void {
    state.playing = false;
    state.currentTime = 0;
    state.duration = 0;
    state.seekValue = 0;
    if (state.audioEl) {
      state.audioEl.pause();
      state.audioEl.currentTime = 0;
    }
  }

  function applyCurrentPageAudio(): void {
    resetPlayback();
    state.audioUrl = getCoursewareAudioUrl(state.audioList, coursewareIndex.value);
  }

  async function loadAudioList(): Promise<void> {
    if (!topicId.value) {
      state.audioList = [];
      state.audioUrl = '';
      resetPlayback();
      return;
    }

    state.loading = true;
    resetPlayback();
    state.audioUrl = '';

    try {
      state.audioList = await requestCoursewareAudio({
        topicId: topicId.value,
      });
      applyCurrentPageAudio();
    } catch (error) {
      state.audioList = [];
      state.audioUrl = '';
    } finally {
      state.loading = false;
    }
  }

  async function togglePlay(): Promise<void> {
    const audio = state.audioEl;
    if (!audio || !state.audioUrl) {
      return;
    }
    if (state.playing) {
      audio.pause();
      state.playing = false;
      return;
    }
    try {
      await audio.play();
      state.playing = true;
    } catch (error) {
      state.playing = false;
    }
  }

  function toggleMute(): void {
    const audio = state.audioEl;
    if (!audio) {
      return;
    }
    state.muted = !state.muted;
    audio.muted = state.muted;
  }

  function onTimeUpdate(): void {
    const audio = state.audioEl;
    if (!audio || state.isSeeking) {
      return;
    }
    state.currentTime = audio.currentTime;
    state.seekValue = state.duration ? (audio.currentTime / state.duration) * 100 : 0;
  }

  function onLoadedMetadata(): void {
    const audio = state.audioEl;
    if (!audio) {
      return;
    }
    state.duration = Number.isFinite(audio.duration) ? audio.duration : 0;
  }

  function onAudioEnded(): void {
    state.playing = false;
    state.currentTime = 0;
    state.seekValue = 0;
    if (state.audioEl) {
      state.audioEl.currentTime = 0;
    }
  }

  function onSeekInput(event: Event): void {
    const audio = state.audioEl;
    const target = event.target as HTMLInputElement;
    const percent = Number(target.value);
    state.seekValue = percent;
    if (!state.duration) {
      return;
    }
    const nextTime = (percent / 100) * state.duration;
    state.currentTime = nextTime;
    if (audio) {
      audio.currentTime = nextTime;
    }
  }

  function onSeekStart(): void {
    state.isSeeking = true;
    window.addEventListener('pointerup', onSeekPointerEnd);
  }

  function onSeekPointerEnd(): void {
    state.isSeeking = false;
    window.removeEventListener('pointerup', onSeekPointerEnd);
  }

  function onSeekEnd(): void {
    onSeekPointerEnd();
  }

  watch(topicId, () => {
    void loadAudioList();
  });

  watch(coursewareIndex, () => {
    if (state.audioList.length) {
      applyCurrentPageAudio();
    }
  });

  onMounted(() => {
    void loadAudioList();
  });

  onBeforeUnmount(() => {
    onSeekPointerEnd();
    resetPlayback();
  });

  return {
    audioUrl: computed(() => state.audioUrl),
    loading: computed(() => state.loading),
    playing: computed(() => state.playing),
    muted: computed(() => state.muted),
    seekValue: computed(() => state.seekValue),
    visible,
    currentTimeLabel,
    durationLabel,
    progressPercent,
    bindAudioRef,
    togglePlay,
    toggleMute,
    onTimeUpdate,
    onLoadedMetadata,
    onAudioEnded,
    onSeekInput,
    onSeekStart,
    onSeekEnd,
  };
}
